import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import EventIcon from '@mui/icons-material/Event';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StarIcon from '@mui/icons-material/Star';

import { superadminService } from '../../../services/superadmin.service';
import { theme } from '../../../theme';

const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

const displayName = (u) => {
  if (!u) return '';
  const full = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return full || u.email || '';
};

const getActivityChipSx = (action) => {
  const a = (action || '').toLowerCase();
  if (a.includes('create')) {
    return {
      backgroundColor: `${theme.colors.success[500]}25`,
      color: theme.colors.success[600],
      border: `1px solid ${theme.colors.success[500]}`,
    };
  }
  if (a.includes('update')) {
    return {
      backgroundColor: `${theme.colors.info[500]}25`,
      color: theme.colors.info[600],
      border: `1px solid ${theme.colors.info[500]}`,
    };
  }
  if (a.includes('delete')) {
    return {
      backgroundColor: `${theme.colors.error[500]}25`,
      color: theme.colors.error[600],
      border: `1px solid ${theme.colors.error[500]}`,
    };
  }
  return {
    backgroundColor: `${theme.colors.warning[500]}25`,
    color: theme.colors.warning[600],
    border: `1px solid ${theme.colors.warning[500]}`,
  };
};

const StatCard = ({ label, value, icon, gradient, trend, subtitle, sublabel = "Ce mois-ci" }) => (
  <Card
    sx={{
      borderRadius: 3,
      background: gradient,
      border: 'none',
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 0.2s ease',
      '&:hover': { transform: 'translateY(-4px)' },
    }}
  >
    <CardContent sx={{ position: 'relative', zIndex: 1, p: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              flex: '0 0 auto',
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: { xs: 20, sm: 24 } } })}
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.95)',
              fontWeight: 800,
              lineHeight: 1.1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.7rem', sm: '0.875rem' } // Changed xs font size
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box // Added new Box with StarIcon
          sx={{
            display: { xs: 'none', sm: 'flex' },
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.15)',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <StarIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.8)' }} />
        </Box>
        {trend && (
          <Chip
            icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
            label={trend}
            size="small"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 24,
            }}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, justifyContent: { xs: 'flex-start', sm: 'center' } }}> {/* Changed justifyContent */}
        <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.25rem', sm: '2rem' }, color: '#fff', lineHeight: 1 }}> {/* Changed xs font size */}
          {value}
        </Typography>
        <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.85rem' }, color: 'rgba(255,255,255,0.85)', fontWeight: 800 }}> {/* Changed xs font size and used sublabel */}
          {sublabel}
        </Typography>
      </Box>
      {subtitle && (
        <Typography
          sx={{
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.75)',
            mt: 0.5,
            fontWeight: 700,
            textAlign: 'center',
          }}
        >
          {subtitle}
        </Typography>
      )}
      {trend && (
        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
          Ce mois-ci
        </Typography>
      )}
    </CardContent>
  </Card>
);

const SuperAdminOverview = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setError('');
        setLoading(true);
        const res = await superadminService.stats();
        setStats(res?.data || null);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography sx={{ color: theme.colors.error[600], fontWeight: 800 }}>{error}</Typography>;
  }

  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', md: '1.75rem' }, mb: 2, color: theme.colors.text.primary }}>
        Admin Dashboard
      </Typography>
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatCard
            label="Total Salons"
            value={stats?.salons_total_ce_mois ?? stats?.salons_month ?? 0}
            icon={<StoreIcon />}
            gradient={`linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%)`}
            subtitle={`/ ${stats?.salons_total ?? 0}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatCard
            label="Responsables"
            value={stats?.responsables_total_ce_mois ?? stats?.responsables_month ?? 0}
            icon={<PersonIcon />}
            gradient={`linear-gradient(135deg, ${theme.colors.secondary[400]} 0%, ${theme.colors.secondary[700]} 100%)`}
            subtitle={`/ ${stats?.responsables_total ?? 0}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatCard
            label="Employés"
            value={stats?.employees_total_ce_mois ?? stats?.employees_month ?? 0}
            icon={<EventIcon />}
            gradient={`linear-gradient(135deg, ${theme.colors.success[500]} 0%, ${theme.colors.success[700]} 100%)`}
            subtitle={`/ ${stats?.employees_total ?? 0}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatCard
            label="Clients"
            value={stats?.clients_total_ce_mois ?? stats?.clients_month ?? 0}
            icon={<PeopleIcon />}
            gradient={`linear-gradient(135deg, ${theme.colors.info[500]} 0%, ${theme.colors.info[700]} 100%)`}
            subtitle={`/ ${stats?.clients_total ?? 0}`}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              backgroundColor: '#fff',
              border: `1px solid ${theme.colors.border.light}`,
            }}
          >
            <CardContent>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 2, color: theme.colors.text.primary }}>
                Salons Récents
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {(stats?.recent_salons || []).map((salon) => (
                  <Box
                    key={salon.salon_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: theme.colors.grey[50],
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: theme.colors.grey[100] },
                    }}
                  >
                    <Typography sx={{ fontSize: '0.875rem', color: theme.colors.text.primary }}>
                      {salon?.name || ''}
                    </Typography>
                    <Chip
                      label={formatDateTime(salon?.created_at)}
                      size="small"
                      sx={{
                        backgroundColor: `${theme.colors.success[500]}25`,
                        color: theme.colors.success[600],
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 22,
                        border: `1px solid ${theme.colors.success[500]}`,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              backgroundColor: '#fff',
              border: `1px solid ${theme.colors.border.light}`,
            }}
          >
            <CardContent>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 2, color: theme.colors.text.primary }}>
                Responsables Récents
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {(stats?.recent_responsables || []).map((u) => (
                  <Box
                    key={u.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: theme.colors.grey[50],
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: theme.colors.grey[100] },
                    }}
                  >
                    <Typography sx={{ fontSize: '0.875rem', color: theme.colors.text.primary }}>
                      {displayName(u)}
                    </Typography>
                    <Chip
                      label={formatDateTime(u?.created_at)}
                      size="small"
                      sx={{
                        backgroundColor: `${theme.colors.success[500]}25`,
                        color: theme.colors.success[600],
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 22,
                        border: `1px solid ${theme.colors.success[500]}`,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              backgroundColor: '#fff',
              border: `1px solid ${theme.colors.border.light}`,
            }}
          >
            <CardContent>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 2, color: theme.colors.text.primary }}>
                Activités Récentes
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {(stats?.recent_activity || []).map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: theme.colors.grey[50],
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: theme.colors.grey[100] },
                    }}
                  >
                    <Typography sx={{ fontSize: '0.875rem', color: theme.colors.text.primary }}>
                      {item?.description || item?.action || ''}
                    </Typography>
                    <Chip
                      label={formatDateTime(item?.timestamp)}
                      size="small"
                      sx={{
                        ...getActivityChipSx(item?.action),
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 22,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SuperAdminOverview;
